import React from "react";
import VideoPlayer from "./video/VideoPlayer";
import MediaPanel from "./player/MediaPanel";
import TimeSelector from "./player/TimeSelector";
import { GPSInformation, SLKInformation } from "./locationInformation";
import MapControl from "./map/MapControl";
import SplitterLayout from "react-splitter-layout";
import RoadSafetyPanel from "./roadSafety/RoadSafetyPanel";
import mapHelper from "@mrwa/rime-replacement/infrastructure/mapHelper";
import { ReactComponent as Logo } from "./roadview.svg";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretRight,
  faFilm,
  faMapPin,
  faQuestion,
  faSignOutAlt,
  faStream,
} from "@fortawesome/free-solid-svg-icons";
import gpsData from "./data/gpsData";
import { LayerPanel } from "./layerInformation/LayerPanel";
import AddCoordinateToolBar from "./coordinateList/AddCoordinateToolBar";
import CoordinateList from "./coordinateList/CoordinateList";
import { setDefaultOptions } from "esri-loader";
import Playlist from "./playlist/Playlist";
import { setModalRoot } from "@mrwa/rime-replacement/esriControls/general/Dialog";
import ApplicationModal from "@mrwa/rime-replacement/esriControls/general/Dialog";
import { ApplicationMessageModal } from "@mrwa/rime-replacement/esriControls/general/ApplicationMessage";
import axios from "axios";
import { getAuthToken } from "./Auth";
import { AuthContext, CrashContext } from "./context";
import crashState from "./roadSafety/crashState";
import ScreenshotTool from "./videoTools/ScreenshotTool";

import "./App.css";
import "@mrwa/rime-replacement/colours.css";
import "./fonts.css";
import "react-splitter-layout/lib/index.css";

// configure esri-loader to use version 4.21 from the ArcGIS CDN
// the default of 4.17 sometimes freezes chrome <- also happens on 4.21, but 4.21 is better for other things
setDefaultOptions({ version: "4.21" });
setModalRoot(document.getElementById("modal-root"));

class App extends React.Component {
  static contextType = AuthContext;
  constructor(props) {
    super(props);

    this.state = {
      isRoadViewUser: null,
      playerContext: null,
      mapSettings: null,
      isCrashUser: null,
      //set crashContext in state rather than on render to avoid context consumers over re-rendering
      crashContext: {
        mapInitialised: false,
        isCrashUser: false,
      },
      playing: false,
      sidebarExpanded: false,
    };

    this.gpsData = new gpsData();
    this.roadViewManual = `${process.env.PUBLIC_URL}/Road View User Guide.pdf`;
    this.toggleSidebar = this.toggleSidebar.bind(this);
    this.handleSetPlayerContext = this.handleSetPlayerContext.bind(this);
    this.mapInitialised = this.mapInitialised.bind(this);

    this.headerRef = React.createRef();
    this.videoRef = React.createRef();

    if (process.env.NODE_ENV && process.env.NODE_ENV === "development")
      // hot reload doesn't reset maphelper, so set a unique instance to stop errors
      mapHelper.setInstance(Math.random().toString());
  }

  async componentDidMount() {
    const settingsResponse = await fetch(
      `${process.env.PUBLIC_URL}/mapSettings.json`
    );
    const mapSettings = await settingsResponse.json();
    this.setState({ mapSettings });

    try {
      const groups = await axios.get(mapSettings.groupsQueryUrl, {
        headers: {
          Authorization: `Bearer ${await getAuthToken(
            this.context.accountId,
            mapSettings.groupsQueryB2CScopes
          )}`,
        },
      });

      if (
        groups.data.find(
          (group) => group.displayName === mapSettings.roadViewGroup
        )
      ) {
        const isCrashUser =
          groups.data.find(
            (group) => group.displayName === mapSettings.crashDataGroup
          ) != null;
        this.setState({
          isRoadViewUser: true,
          isCrashUser,
          crashContext: {
            ...this.state.crashContext,
            isCrashUser,
          },
        });

        if (isCrashUser) {
          crashState.initialise({
            roadViewVehicleDetailsUrl: `${mapSettings.crashDataApiUrl}/QueryCrashVehicleDetails`,
            accountId: this.context.accountId,
          });
        }
      } else {
        this.setState({
          isRoadViewUser: false,
          roadViewUserMessage: "You are not authorised to access Road View",
        });
        return;
      }
    } catch {
      this.setState({
        isRoadViewUser: false,
        roadViewUserMessage: "Unable to verify Road View access permissions",
      });
      return;
    }

    //have to offset the overPanels to allow crash components to render in relation to map position.
    //nasty using direct dom manipulation, but can't work out a styling that allows for everything to work
    this.offsetCss = document.createElement("style");
    document.body.appendChild(this.offsetCss);
    window.addEventListener("resize", this.changeOffsetCss.bind(this));
  }

  componentDidUpdate() {
    this.changeOffsetCss();
  }

  changeOffsetCss() {
    if (!this.headerRef.current || !this.offsetCss) return;
    this.offsetCss.innerHTML = `.mapControl div.overPanel { top: ${this.headerRef.current.clientHeight}px; }`;
  }

  handleSetPlayerContext(playerContext) {
    playerContext.onContextStateChanged((context) =>
      this.setState({ playing: context.playing })
    );
    this.setState({ playerContext });
  }

  toggleSidebar() {
    const collapse = this.state.sidebarExpanded;
    this.setState({ sidebarExpanded: !collapse });
  }

  mapInitialised() {
    this.setState({
      crashContext: { ...this.state.crashContext, mapInitialised: true },
    });
  }

  render() {
    if (!this.state.isRoadViewUser) {
      return (
        <div>
          <span style={{ marginRight: "1em" }}>
            {this.state.roadViewUserMessage || "Checking Road View access..."}
          </span>
          <button
            type="button"
            className="logoutButton button"
            onClick={this.props.signOut}
            title="Logout from Road View"
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
          </button>
        </div>
      );
    }

    const sidebarClass = classNames({
      sidebar: true,
      expanded: this.state.sidebarExpanded,
    });

    const userType = (() => {
      switch (this.state.isCrashUser) {
        case false:
          return "general user";
        case true:
          return "crash user";
        default:
          return "Querying user type...";
      }
    })();

    return (
      <CrashContext.Provider
        value={{ ...this.context, ...this.state.crashContext }}
      >
        <div className="App fullscreen roadView">
          <div className="header" ref={this.headerRef}>
            <div className="appIcon">
              <Logo className="logo" />
            </div>
            <div className="title">
              <h1>Road View</h1>
              <span className="version">
                {this.state.mapSettings &&
                  `version: ${this.state.mapSettings.version}`}
              </span>
            </div>
            <div className="loginInfo">
              <div>
                <span className="userName">
                  {`user: ${this.context.username}`}
                </span>
                <span>{userType}</span>
              </div>
            </div>
            <GPSInformation
              gpsData={this.gpsData}
              playerContext={this.state.playerContext}
            />
            <SLKInformation
              playerContext={this.state.playerContext}
              irisRoadLayerConfig={
                this.state.mapSettings && this.state.mapSettings.irisRoadLayer
              }
            />
            <div className="mediaControls">
              <MediaPanel playerContext={this.state.playerContext} />
              <TimeSelector playerContext={this.state.playerContext} />
            </div>
            <div className="userLinks">
              <button
                type="button"
                className="logoutButton button"
                onClick={this.props.signOut}
                title="Logout from Road View"
              >
                <FontAwesomeIcon icon={faSignOutAlt} />
              </button>
              <a
                className="userManualLink button"
                href={this.roadViewManual}
                target="_blank"
                rel="noopener noreferrer"
                title="User Guide"
              >
                <FontAwesomeIcon icon={faQuestion} />
              </a>
            </div>
          </div>
          <div className="body">
            <SplitterLayout
              percentage={true}
              secondaryInitialSize={60}
              secondaryMinSize={33}
              primaryMinSize={20}
            >
              <div className="mapAndLocationColumn">
                <MapControl
                  playerContext={this.state.playerContext}
                  mapSettings={this.state.mapSettings}
                  playing={this.state.playing}
                  mapInitialised={this.mapInitialised}
                />
              </div>
              <div className="videoAndInformationColumn">
                <div className="videoToolbar">
                  <ScreenshotTool
                    videoRef={this.videoRef}
                    playerContext={this.state.playerContext}
                  />
                  <AddCoordinateToolBar
                    playerContext={this.state.playerContext}
                    irisRoadLayerConfig={
                      this.state.mapSettings &&
                      this.state.mapSettings.irisRoadLayer
                    }
                  />
                </div>
                <VideoPlayer
                  setPlayerContext={this.handleSetPlayerContext}
                  gpsData={this.gpsData}
                  nomadApiUrl={
                    this.state.mapSettings && this.state.mapSettings.nomadApiUrl
                  }
                  ref={this.videoRef}
                />
                <div className={sidebarClass}>
                  <div
                    className="expander button light"
                    onClick={this.toggleSidebar}
                    title={`${
                      this.state.sidebarExpanded ? "Collapse" : "Expand"
                    } Sidebar`}
                  >
                    <FontAwesomeIcon
                      icon={this.state.sidebarExpanded ? faCaretRight : faFilm}
                      transform="rotate-270"
                    />
                  </div>
                  <div className="content">
                    <fieldset className="innerContent">
                      <legend>
                        <h3>
                          <FontAwesomeIcon icon={faMapPin} size="sm" />{" "}
                          Coordinate List
                        </h3>
                      </legend>
                      <CoordinateList
                        playerContext={this.state.playerContext}
                      />
                    </fieldset>
                    <fieldset className="innerContent">
                      <legend>
                        <h3>
                          <FontAwesomeIcon icon={faStream} size="sm" /> Playlist
                        </h3>
                      </legend>
                      <Playlist playerContext={this.state.playerContext} />
                    </fieldset>
                  </div>
                </div>
                <div className="informationPanels">
                  <div>
                    <RoadSafetyPanel
                      playerContext={this.state.playerContext}
                      irisRoadLayerConfig={
                        this.state.mapSettings &&
                        this.state.mapSettings.irisRoadLayer
                      }
                      speedLimitLayerConfig={
                        this.state.mapSettings &&
                        this.state.mapSettings.speedLimitLayer
                      }
                      curveSurveyLayerConfig={
                        this.state.mapSettings &&
                        this.state.mapSettings.curveSurveyLayer
                      }
                      pavementDetailsLayerConfig={
                        this.state.mapSettings &&
                        this.state.mapSettings.pavementDetailsLayer
                      }
                      roadsideHazardRatingLayerConfig={
                        this.state.mapSettings &&
                        this.state.mapSettings.roadsideHazardRatingLayer
                      }
                      geometryAlignmentLayerConfig={
                        this.state.mapSettings &&
                        this.state.mapSettings.geometryAlignmentLayer
                      }
                    />
                  </div>
                  <div>
                    <LayerPanel
                      playerContext={this.state.playerContext}
                      excludedLayers={
                        this.state.mapSettings &&
                        this.state.mapSettings.excludedLayerDetails
                      }
                    />
                  </div>
                </div>
              </div>
            </SplitterLayout>
          </div>
          <ApplicationMessageModal />
          {ApplicationModal}
        </div>
      </CrashContext.Provider>
    );
  }
}

export default App;
